import { useQuery } from '@tanstack/react-query';
import { supabase } from '../config/supabase';
import { telemetryService } from '../services/telemetry/telemetryService';
import {
  buildStrokePowerSeries,
  compareHullStability,
  type HullStabilityComparison,
} from '../utils/hullStability/filterStrokePowers';
import {
  buildOrderedStrokeTimestamps,
  type StrokeWindowRow,
} from '../utils/hullStability/strokeAlignment';
import type { StrokeStabilityRecord } from './useStrokeStabilityData';

export type HullStabilitySessionSnapshot = {
  comparison: HullStabilityComparison;
  strokeMetricRows: Record<string, unknown>[];
  orderedStrokeTimestamps: number[];
  boatMotionMinTimestamp: number | null;
  strokeWindows: StrokeWindowRow[];
};

function countSetStrokes(stabilityRows: StrokeStabilityRecord[] | undefined): number {
  return stabilityRows?.filter((row) => row.clean_for_analysis).length ?? 0;
}

export function useHullStabilityComparison(
  sessionId: string | null,
  stabilityRows: StrokeStabilityRecord[] | undefined
) {
  const stabilitySetCount = countSetStrokes(stabilityRows);

  return useQuery({
    queryKey: [
      'session',
      'hull-stability-comparison',
      sessionId,
      stabilityRows?.length ?? 0,
      stabilitySetCount,
    ],
    enabled: !!sessionId && !!stabilityRows && stabilityRows.length > 0,
    staleTime: 30_000,
    queryFn: async (): Promise<HullStabilitySessionSnapshot> => {
      if (!sessionId || !stabilityRows?.length) {
        throw new Error('Hull stability comparison requires scored strokes');
      }
      const resolvedId = await telemetryService.resolveSessionId(sessionId);
      if (!resolvedId) throw new Error('Session not found');

      const { data, error } = await supabase
        .from('telemetry_stroke_metrics')
        .select(
          'timestamp, rower_1_power, rower_2_power, rower_3_power, rower_4_power, rower_5_power, rower_6_power, rower_7_power, rower_8_power'
        )
        .eq('session_id', resolvedId)
        .order('timestamp', { ascending: true });

      if (error) throw error;
      const strokeMetricRows = (data ?? []) as Record<string, unknown>[];
      const strokeSeries = buildStrokePowerSeries(strokeMetricRows);

      const { data: boatRow, error: boatError } = await supabase
        .from('telemetry_boat_motion')
        .select('timestamp')
        .eq('session_id', resolvedId)
        .order('timestamp', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (boatError) throw boatError;
      const boatMotionMinTimestamp = boatRow?.timestamp != null ? Number(boatRow.timestamp) : null;

      const { data: windowRows, error: windowsError } = await supabase
        .from('stroke_windows')
        .select('stroke_index, window_start_ms, window_end_ms')
        .eq('session_id', resolvedId)
        .order('stroke_index', { ascending: true });

      if (windowsError) throw windowsError;

      const strokeWindows = (windowRows ?? []) as StrokeWindowRow[];

      return {
        comparison: compareHullStability(strokeSeries, stabilityRows),
        strokeMetricRows,
        orderedStrokeTimestamps: buildOrderedStrokeTimestamps(strokeMetricRows),
        boatMotionMinTimestamp,
        strokeWindows,
      };
    },
  });
}
